import { ComponentType } from '@angular/cdk/portal';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { UserService } from 'src/app/core/services/user/user.service';
import { NotificationService } from 'src/app/features/modules/notification/services/notification.service';
import { AchievementsService } from '../achievements.service';
import { BadgesFeatureService } from '../badges-feature';
import { AchievementsRevokedDialogComponent } from '../achievements-revoked/achievements-revoked-dialog.component';
import { AchievementsCelebrationDialogComponent } from './achievements-celebration-dialog.component';

/** Navigation-driven checks hit the server at most this often. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Celebrates badges the logged-in user earned since the last celebration and then shows the ones
 * an admin revoked. The server keeps the watermark and returns nothing until the one-time welcome
 * was seen, so the two never overlap.
 */
@Injectable({ providedIn: 'root' })
export class AchievementsCelebrationService {
  private lastCheckAt = 0;
  private busy = false;

  constructor(
    private achievementsService: AchievementsService,
    private userService: UserService,
    private matDialog: MatDialog,
    private ngZone: NgZone,
    private badgesFeature: BadgesFeatureService,
    notificationService: NotificationService
  ) {
    // Fires on SignalR notifications (badge awards included) and on hub reconnects. The award
    // transaction may still be committing when the push arrives, hence the short delay. The hub
    // callback runs outside Angular's zone; a dialog opened there renders empty, so re-enter it.
    notificationService.onChangeNotification.subscribe(() =>
      setTimeout(() => this.ngZone.run(() => this.check(true)), 3000)
    );
  }

  check(force = false): void {
    if (this.busy || !this.userService.userHasLoggedIn || !this.badgesFeature.isEnabled) {
      return;
    }
    if (!force && Date.now() - this.lastCheckAt < CHECK_INTERVAL_MS) {
      return;
    }
    this.busy = true;
    this.lastCheckAt = Date.now();

    this.achievementsService.getCelebrationState().subscribe(
      state => {
        const hasAwards = !!state?.badges?.length;
        const hasRevokes = !!state?.revokedBadges?.length;
        if ((!hasAwards && !hasRevokes) || !state.asOf) {
          this.busy = false;
          return;
        }
        // Profile cards and the achievements page reload, so the page behind shows the change.
        this.achievementsService.badgesChanged$.next();

        // Celebration first, then the revoke notice; one dismiss covers both.
        const dismiss = () =>
          this.achievementsService.dismissCelebration(state.asOf).subscribe(
            () => (this.busy = false),
            () => (this.busy = false)
          );
        const showRevoked = () =>
          hasRevokes
            ? this.openDialog(AchievementsRevokedDialogComponent, state.revokedBadges).subscribe(dismiss)
            : dismiss();

        if (hasAwards) {
          this.openDialog(AchievementsCelebrationDialogComponent, state).subscribe(showRevoked);
        } else {
          showRevoked();
        }
      },
      () => (this.busy = false)
    );
  }

  private openDialog<T>(component: ComponentType<T>, data: unknown): Observable<unknown> {
    return this.matDialog
      .open(component, {
        data,
        autoFocus: false,
        panelClass: 'badge-dialog-panel',
        width: '480px',
        maxWidth: 'calc(100vw - 32px)',
      })
      .afterClosed();
  }
}
